import React, { useState, useEffect, useCallback } from 'react';
import './Products.css';
import './AllSellStyles.css';
import './CommonStyles.css';
import {
  getTaxReportSummary,
  getTaxDetails,
  getTaxReportFilterOptions,
} from '../../services/reportApi';
import { useBusinessSettings } from '../../contexts/BusinessSettingsContext';

const toYmd = (d) => {
  const date = new Date(d);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const defaultDates = (() => {
  const s = new Date(new Date().getFullYear(), 0, 1);
  const e = new Date();
  return { start: toYmd(s), end: toYmd(e) };
})();

const inputStyle = {
  padding: '0.4rem 0.6rem',
  border: '1px solid #cbd5e1',
  borderRadius: '6px',
  fontSize: '0.8125rem',
  color: 'var(--etpos-text, #334155)',
  outline: 'none',
  minWidth: '120px',
};

const TaxReport = () => {
  const { formatCurrency } = useBusinessSettings();
  const [activeSubTab, setActiveSubTab] = useState('input');
  const [entriesPerPage, setEntriesPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [tabData, setTabData] = useState({
    input: { taxes: [], transactions: [], totals: {} },
    output: { taxes: [], transactions: [], totals: {} },
    expense: { taxes: [], transactions: [], totals: {} },
  });
  const [filterOptions, setFilterOptions] = useState({ locations: [] });
  const [filters, setFilters] = useState({
    start_date: defaultDates.start,
    end_date: defaultDates.end,
    location_id: '',
    contact_id: '',
  });

  const subTabs = [
    { id: 'input',   label: 'Input Tax',   icon: 'fa-arrow-circle-down', type: 'purchase' },
    { id: 'output',  label: 'Output Tax',  icon: 'fa-arrow-circle-up',   type: 'sell' },
    { id: 'expense', label: 'Expense Tax', icon: 'fa-minus-circle',       type: 'expense' },
  ];

  const fetchSummary = useCallback(async () => {
    try {
      const res = await getTaxReportSummary(filters);
      if (res.success) setSummary(res.data);
    } catch (e) {
      /* console.error('Summary error:', e); */
    }
  }, [filters]);

  const fetchTabData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [inputRes, outputRes, expenseRes] = await Promise.all([
        getTaxDetails({ ...filters, type: 'purchase' }),
        getTaxDetails({ ...filters, type: 'sell' }),
        getTaxDetails({ ...filters, type: 'expense' }),
      ]);
      setTabData({
        input: inputRes.success ? inputRes.data : { taxes: [], transactions: [], totals: {} },
        output: outputRes.success ? outputRes.data : { taxes: [], transactions: [], totals: {} },
        expense: expenseRes.success ? expenseRes.data : { taxes: [], transactions: [], totals: {} },
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadAll = useCallback(() => {
    fetchSummary();
    fetchTabData();
  }, [fetchSummary, fetchTabData]);

  useEffect(() => {
    loadAll();
    getTaxReportFilterOptions()
      .then(r => { if (r.success) setFilterOptions(r.data); })
      .catch(() => {});
  }, []);

  const applyFilters = () => {
    loadAll();
  };

  const activeTab = tabData[activeSubTab] || { taxes: [], transactions: [], totals: {} };
  const activeTransactions = activeTab.transactions || [];
  const activeTaxes = activeTab.taxes || [];

  const filteredTransactions = activeTransactions.filter(row =>
    !searchTerm ||
    (row.contact_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (row.ref_no || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (row.invoice_no || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (row.tax_number || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const netTax = summary?.overall?.net_tax ?? 0;
  const netTaxFormatted = summary?.overall?.net_tax_formatted && summary?.overall?.net_tax_formatted !== '$ 0.00' ? summary?.overall?.net_tax_formatted : formatCurrency(0);

  const paginated = filteredTransactions.slice(0, entriesPerPage);

  const isInput = activeSubTab === 'input';
  const isOutput = activeSubTab === 'output';
  const isExpense = activeSubTab === 'expense';

  return (
    <div style={{ paddingTop: '0.5rem' }}>

      {/* ── Overall Tax Summary card ── */}
      <div style={{
        background: 'var(--etpos-card, #fff)',
        border: '1px solid var(--etpos-border, #e2e8f0)',
        borderRadius: '10px',
        padding: '1.25rem 1.5rem',
        marginBottom: '1.25rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        flexWrap: 'wrap',
      }}>
        <span style={{ color: 'var(--etpos-text-muted, #64748b)', fontSize: '0.95rem' }}>
          Overall (Input - Output - Expense):
        </span>
        <span style={{ color: netTax >= 0 ? '#22c55e' : '#ef4444', fontWeight: '600', fontSize: '1.05rem' }}>
          {netTaxFormatted}
        </span>
        <i className="fa fa-info-circle" style={{ color: '#0ea5e9', marginLeft: '0.25rem' }} />
      </div>

      {/* ── Sub-tabs ── */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--etpos-border, #e2e8f0)', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            style={{
              padding: '0.75rem 1rem',
              background: 'none',
              border: 'none',
              borderBottom: activeSubTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeSubTab === tab.id ? '#3b82f6' : '#64748b',
              fontWeight: activeSubTab === tab.id ? '600' : '500',
              fontSize: '0.875rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <i className={`fa ${tab.icon}`} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Main table card ── */}
      <div style={{
        background: 'var(--etpos-card, #fff)',
        border: '1px solid var(--etpos-border, #e2e8f0)',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>

        {/* ── Filter row ── */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1.25rem',
          borderBottom: '1px solid var(--etpos-border, #e2e8f0)',
          flexWrap: 'wrap',
        }}>
          <select
            value={filters.location_id}
            onChange={e => setFilters(f => ({ ...f, location_id: e.target.value }))}
            style={{ ...inputStyle, background: 'var(--etpos-card, #fff)', cursor: 'pointer' }}
          >
            <option value="">All Locations</option>
            {Array.isArray(filterOptions.locations)
              ? filterOptions.locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)
              : Object.entries(filterOptions.locations || {}).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.8125rem', color: 'var(--etpos-text-muted, #64748b)', whiteSpace: 'nowrap' }}>From:</label>
            <input
              type="date"
              value={filters.start_date}
              onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))}
              style={{
                padding: '0.4rem 0.75rem',
                border: '1px solid var(--etpos-border, #e2e8f0)',
                borderRadius: '6px',
                fontSize: '0.8125rem',
                outline: 'none',
                color: 'var(--etpos-text, #334155)',
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.8125rem', color: 'var(--etpos-text-muted, #64748b)', whiteSpace: 'nowrap' }}>To:</label>
            <input
              type="date"
              value={filters.end_date}
              onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))}
              style={{
                padding: '0.4rem 0.75rem',
                border: '1px solid var(--etpos-border, #e2e8f0)',
                borderRadius: '6px',
                fontSize: '0.8125rem',
                outline: 'none',
                color: 'var(--etpos-text, #334155)',
              }}
            />
          </div>
          <button
            onClick={applyFilters}
            style={{
              padding: '0.4rem 1rem',
              background: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.8125rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#2563eb'}
            onMouseLeave={e => e.currentTarget.style.background = '#3b82f6'}
          >
            <i className="fa fa-search" />
            Search
          </button>
        </div>

        {/* ── Table controls ── */}
        <div className="table-controls" style={{ borderBottom: '1px solid var(--etpos-border, #e2e8f0)', padding: '0.75rem 1.25rem' }}>
          <div className="table-controls-left">
            <div className="entries-select">
              <span>Show</span>
              <select value={entriesPerPage} onChange={e => setEntriesPerPage(Number(e.target.value))}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>

          <div className="table-controls-right">
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button
                className="export-btn"
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.4rem 0.8rem', border: '1px solid var(--etpos-border, #e2e8f0)',
                  borderRadius: '6px', background: 'var(--etpos-card, #fff)', cursor: 'pointer',
                  fontSize: '0.85rem', color: 'var(--etpos-text, #334155)',
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu" style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: '0.25rem',
                  background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px',
                  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 10, minWidth: '160px',
                }}>
                  {[
                    { icon: 'fa-file-excel-o', label: 'Export CSV' },
                    { icon: 'fa-file-excel-o', label: 'Export Excel' },
                    { icon: 'fa-print',        label: 'Print' },
                    { icon: 'fa-table',        label: 'Column visibility' },
                    { icon: 'fa-file-pdf-o',   label: 'Export PDF' },
                  ].map((item, i) => (
                    <button
                      key={i}
                      onClick={() => setShowExportDropdown(false)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        width: '100%', padding: '0.5rem 1rem', border: 'none',
                        background: 'none', textAlign: 'left', cursor: 'pointer',
                        fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)',
                      }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <i className={`fa ${item.icon}`} style={{ width: '14px', textAlign: 'center' }} />
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <input
              type="text"
              placeholder="Search ..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                padding: '0.4rem 0.75rem',
                border: '1px solid var(--etpos-border, #e2e8f0)',
                borderRadius: '6px',
                fontSize: '0.8125rem',
                outline: 'none',
                width: '180px',
              }}
            />
          </div>
        </div>

        {loading && <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--etpos-text-muted, #64748b)' }}>Loading...</div>}
        {error && <div style={{ padding: '0.75rem 1rem', background: '#fef2f2', color: '#dc2626', fontSize: '0.875rem' }}>{error}</div>}

        {/* ── Data table ── */}
        {!loading && (
          <div style={{ overflowX: 'auto' }}>
            <table className="products-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 'auto' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--etpos-border, #e2e8f0)' }}>
                  <th style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600' }}>Date</th>
                  <th style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600' }}>{isOutput ? 'Invoice No' : 'Reference No'}</th>
                  <th style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600' }}>{isOutput ? 'Customer' : 'Supplier'}</th>
                  <th style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600' }}>Tax Number</th>
                  <th style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600' }}>Total Amount</th>
                  <th style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600' }}>Payment Method</th>
                  {!isExpense && (
                    <th style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600' }}>Discount</th>
                  )}
                  {activeTaxes.map(tax => (
                    <th key={tax.id} style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600' }}>{tax.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={6 + activeTaxes.length + (isExpense ? 0 : 1)} style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--etpos-text-muted, #94a3b8)', fontSize: '0.9rem' }}>
                      No data available in table
                    </td>
                  </tr>
                ) : (
                  paginated.map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--etpos-border, #f1f5f9)' }}>
                      <td style={{ padding: '0.6rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #334155)' }}>{row.transaction_date || '-'}</td>
                      <td style={{ padding: '0.6rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #334155)' }}>{isOutput ? (row.invoice_no || '-') : (row.ref_no || '-')}</td>
                      <td style={{ padding: '0.6rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #334155)' }}>{row.contact_name || '-'}</td>
                      <td style={{ padding: '0.6rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #334155)' }}>{row.tax_number || '-'}</td>
                      <td style={{ padding: '0.6rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600' }}>{row.total_before_tax_formatted || row.total_before_tax || '-'}</td>
                      <td style={{ padding: '0.6rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #334155)' }}>{row.payment_method || '-'}</td>
                      {!isExpense && (
                        <td style={{ padding: '0.6rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #334155)' }}>{row.discount_amount || '-'}</td>
                      )}
                      {activeTaxes.map(tax => {
                        const val = row.taxes?.['tax_' + tax.id] ?? 0;
                        return (
                          <td key={tax.id} style={{ padding: '0.6rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #334155)' }}>
                            {val > 0 ? formatCurrency(val) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
                {/* Footer totals row */}
                {paginated.length > 0 && (
                  <tr style={{ background: '#f1f5f9', borderTop: '2px solid #e2e8f0', fontWeight: '600' }}>
                    <td style={{ padding: '0.6rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)' }} colSpan={isExpense ? 3 : 4}>
                      Total:
                    </td>
                    <td style={{ padding: '0.6rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)' }}>
                      {activeTab.totals?.total_before_tax ? formatCurrency(activeTab.totals.total_before_tax) : '-'}
                    </td>
                    <td style={{ padding: '0.6rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #334155)' }}>-</td>
                    {!isExpense && (
                      <td style={{ padding: '0.6rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #334155)' }}>-</td>
                    )}
                    {activeTaxes.map(tax => (
                      <td key={tax.id} style={{ padding: '0.6rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)' }}>
                        {activeTab.totals?.['tax_' + tax.id] ? formatCurrency(activeTab.totals['tax_' + tax.id]) : '-'}
                      </td>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Pagination ── */}
        <div className="pagination" style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid var(--etpos-border, #e2e8f0)' }}>
          <div className="pagination-info" style={{ fontSize: '0.8125rem' }}>
            Showing {filteredTransactions.length > 0 ? 1 : 0} to {Math.min(filteredTransactions.length, entriesPerPage)} of {filteredTransactions.length} entries
          </div>
          <div className="pagination-controls">
            <button className="page-btn disabled">Previous</button>
            <button className="page-btn active">1</button>
            <button className="page-btn disabled">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxReport;
